import { tenantAwareApiWrapper, withServiceClient } from './portal-http';

import { trackerClient } from '@terra/shared-rest-clients-tracker';
import type { trackerContract } from '@terra/shared-contracts-tracker';
import type {
  NetworkRequestBatchSchema,
  NetworkRequestSchema,
  PageTrackingSchema,
  TorchSeenNetworkInteractionSchema,
  TrackerBatchReturnDto,
  TrackerReturnDto,
} from '@terra/shared-contracts-tracker';
import type { z } from 'zod';

export const portalTrackerApi = {
  track: (
    payload: z.infer<typeof NetworkRequestSchema>,
  ): Promise<TrackerReturnDto> => {
    if (payload.response.status === 404 && !payload.allowNotFoundResponse) {
      console.debug(`Skipping 404 tracking for ${payload.request.url}`);

      return Promise.resolve({
        success: false,
        message: 'Skipped 404 tracking',
      });
    }

    return tenantAwareApiWrapper<typeof trackerContract.track>({
      endpointFn: (headers) =>
        trackerClient.track({
          body: {
            ...payload,
            credentialId: payload.credentialId ?? null,
          },
          ...withServiceClient(headers, 'tracker'),
        }),
    });
  },

  trackSeen: (
    payload: z.infer<typeof TorchSeenNetworkInteractionSchema>,
  ): Promise<{ success: boolean }> =>
    tenantAwareApiWrapper<typeof trackerContract.trackSeen>({
      endpointFn: (headers) =>
        trackerClient.trackSeen({
          body: {
            ...payload,
            credentialId: payload.credentialId ?? null,
          },
          ...withServiceClient(headers, 'tracker'),
        }),
    }),

  trackBulk: (
    payload: z.infer<typeof NetworkRequestBatchSchema>,
  ): Promise<TrackerBatchReturnDto> => {
    const skippedRequests = payload.requests.filter(
      (request) => request.response.status === 404,
    );
    const requests = payload.requests.filter(
      (request) => request.response.status !== 404,
    );

    if (requests.length === 0) {
      return Promise.resolve({
        success: false,
        message: 'Skipped 404 tracking',
        trackedCount: 0,
        failedCount: 0,
        skippedCount: skippedRequests.length,
      });
    }

    return tenantAwareApiWrapper<typeof trackerContract.trackBulk>({
      endpointFn: (headers) =>
        trackerClient.trackBulk({
          body: {
            applicationId: payload.applicationId,
            requests: requests.map((request) => ({
              ...request,
              applicationId: payload.applicationId,
              credentialId: request.credentialId ?? null,
            })),
          },
          ...withServiceClient(headers, 'tracker'),
        }),
    }).then((result) => ({
      ...result,
      skippedCount: (result.skippedCount ?? 0) + skippedRequests.length,
      skipReason: skippedRequests.length > 0 ? undefined : result.skipReason,
    }));
  },

  trackPages: (payload: z.infer<typeof PageTrackingSchema>) =>
    tenantAwareApiWrapper<typeof trackerContract.trackPages>({
      endpointFn: (headers) =>
        trackerClient.trackPages({
          body: payload,
          ...withServiceClient(headers, 'tracker'),
        }),
    }),

  // Tool HAR endpoints
};
